import { View, Text, TextInput, StyleSheet, ActivityIndicator, Animated, Platform } from 'react-native';

interface HomeDetailsStepProps {
  bedrooms: string;
  bathrooms: string;
  squareFootage: string;
  yearBuilt: string;
  purchaseDate: string;
  onBedroomsChange: (value: string) => void;
  onBathroomsChange: (value: string) => void;
  onSquareFootageChange: (value: string) => void;
  onYearBuiltChange: (value: string) => void;
  onPurchaseDateChange: (value: string) => void;
  enrichingHomeData: boolean;
  enrichmentError: string | null;
  progressMessages: string[];
  currentProgressMessage: number;
  fadeAnim: Animated.Value;
}

export const HomeDetailsStep = ({
  bedrooms,
  bathrooms,
  squareFootage,
  yearBuilt,
  purchaseDate,
  onBedroomsChange,
  onBathroomsChange,
  onSquareFootageChange,
  onYearBuiltChange,
  onPurchaseDateChange,
  enrichingHomeData,
  enrichmentError,
  progressMessages,
  currentProgressMessage,
  fadeAnim
}: HomeDetailsStepProps) => {
  return (
    <View style={styles.stepContainer}>
      <View style={styles.stepHeader}>
        <Text style={styles.stepTitle}>Home Details</Text>
        <Text style={styles.stepSubtitle}>
          {enrichingHomeData 
            ? 'Looking up your home...' 
            : enrichmentError 
              ? 'We couldn\'t find your home details automatically. Please enter them below.'
              : 'We pulled this from public records and similar homes nearby. Fix anything that\'s off, but most people don\'t need to.'}
        </Text>
      </View>
      {enrichingHomeData ? (
        <View style={styles.enrichmentLoadingContainer}>
          <ActivityIndicator size="large" color="#000000" />
          <Animated.View style={{ opacity: fadeAnim }}>
            <Text style={styles.enrichmentLoadingText}>
              {progressMessages[currentProgressMessage]}...
            </Text>
          </Animated.View>
        </View>
      ) : (
        <View style={styles.formFields}>
          <View style={styles.formGrid}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Bedrooms</Text>
              <TextInput
                style={styles.input}
                value={bedrooms}
                onChangeText={(value) => {
                  // Only allow numeric input (integers)
                  const numericValue = value.replace(/[^0-9]/g, '');
                  onBedroomsChange(numericValue);
                }}
                placeholder="Number of bedrooms"
                keyboardType="numeric"
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Bathrooms</Text>
              <TextInput
                style={styles.input}
                value={bathrooms}
                onChangeText={(value) => {
                  // Allow numeric input with one decimal point
                  const numericValue = value.replace(/[^0-9.]/g, '');
                  // Ensure only one decimal point
                  const parts = numericValue.split('.');
                  const filteredValue = parts.length > 2 
                    ? parts[0] + '.' + parts.slice(1).join('')
                    : numericValue;
                  onBathroomsChange(filteredValue);
                }}
                placeholder="Number of bathrooms"
                keyboardType="decimal-pad"
              />
            </View>
          </View>
          <View style={styles.formGrid}>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Square Footage</Text>
              <TextInput
                style={styles.input}
                value={squareFootage}
                onChangeText={(value) => {
                  // Only allow numeric input (integers)
                  const numericValue = value.replace(/[^0-9]/g, '');
                  onSquareFootageChange(numericValue);
                }}
                placeholder="Square footage"
                keyboardType="numeric"
              />
            </View>
            <View style={styles.inputGroup}>
              <Text style={styles.label}>Year Built</Text>
              <TextInput
                style={styles.input}
                value={yearBuilt}
                onChangeText={(value) => {
                  // Only allow numeric input (integers), max 4 digits
                  const numericValue = value.replace(/[^0-9]/g, '').slice(0, 4);
                  onYearBuiltChange(numericValue);
                }}
                placeholder="Year built"
                keyboardType="numeric"
                maxLength={4}
              />
            </View>
          </View>
          <View style={styles.purchaseDateGroup}>
            <Text style={styles.label}>Purchase Date</Text>
            {Platform.OS === 'web' ? (
              <input
                type="date"
                value={purchaseDate || ''}
                onChange={(e) => onPurchaseDateChange(e.target.value)}
                style={{
                  padding: '8px 12px',
                  borderWidth: '1px',
                  borderStyle: 'solid',
                  borderColor: '#d1d5db',
                  borderRadius: '8px',
                  fontSize: '16px',
                  color: '#111827',
                  backgroundColor: '#ffffff',
                  width: '100%',
                  fontFamily: 'inherit',
                  outlineStyle: 'none',
                }}
              />
            ) : (
              <TextInput
                style={styles.input}
                value={purchaseDate}
                onChangeText={onPurchaseDateChange}
                placeholder="YYYY-MM-DD"
                keyboardType="default"
              />
            )}
          </View>
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  stepContainer: {
    gap: 32,
  },
  stepHeader: {
    gap: 8,
  },
  stepTitle: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  stepSubtitle: {
    fontSize: 16,
    color: '#4b5563',
  },
  formGrid: {
    flexDirection: 'row',
    gap: 16,
  },
  formFields: {
    gap: 16,
  },
  inputGroup: {
    gap: 4,
    flex: 1,
  },
  purchaseDateGroup: {
    gap: 4,
  },
  label: {
    fontSize: 14,
    fontWeight: '500',
    color: '#374151',
    marginBottom: 4,
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 16,
    backgroundColor: '#ffffff',
    flex: 1,
  },
  enrichmentLoadingContainer: {
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 48,
    gap: 16,
  },
  enrichmentLoadingText: {
    fontSize: 16,
    color: '#4b5563',
    textAlign: 'center',
  },
});
